import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { getSignedUrls, getWordlist, Word, Wordlist } from '../../../api/word-list';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import useWordlistSearch from '../_hooks/useWordlistSearch';
import useWordlistFavorites from '../_hooks/useWordlistFavorites';
import { Module } from '../../../api/module';
import { useTabs } from '../../../hooks/useTabs';

export const WordlistTab = {
  LIST: 'list',
  FAVORITES: 'favorites',
  SEARCH: 'search',
} as const;

export type WordlistContextValue = {
  wordlist?: Wordlist;
  favoriteIds: string[];
  switched: boolean;
  switchLanguages: () => void;
  toggleFavoriteWord: (id: string) => void;
  selectedWord?: Word;
  setSelectedWord: (word?: Word) => void;
  clearFavoriteWords: () => void;
  currentTab: typeof WordlistTab[keyof typeof WordlistTab];
  handleTabChange: (tab: typeof WordlistTab[keyof typeof WordlistTab]) => void;
  showPreviousWord: (list: EnrichedWord[]) => void;
  showNextWord: (list: EnrichedWord[]) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  searchError?: string;
  searchResults: EnrichedWord[];
  cardsExpanded: boolean;
  setCardsExpanded: (expand: boolean) => void;
  module?: Module;
};

export type EnrichedWord = Word & {
  s3Image?: string;
  s3MaleVoice?: string;
  s3FemaleVoice?: string;
};

const WordlistContext = createContext<WordlistContextValue>({
  wordlist: undefined,
  favoriteIds: [],
  switched: false,
  switchLanguages: () => {},
  toggleFavoriteWord: () => {},
  selectedWord: undefined,
  setSelectedWord: () => {},
  clearFavoriteWords: () => {},
  currentTab: WordlistTab.LIST,
  handleTabChange: () => {},
  showPreviousWord: () => {},
  showNextWord: () => {},
  searchTerm: '',
  setSearchTerm: () => {},
  searchError: undefined,
  searchResults: [],
  cardsExpanded: false,
  setCardsExpanded: () => {},
});

export default WordlistContext;

type SignedUrlsByWord = Record<string, Record<string, string>>;

export function WordlistContextProvider({
  wordlistId,
  module,
  children,
}: PropsWithChildren<{
  wordlistId: string;
  module: Module;
}>) {
  const handleError = useThrowAsyncError();

  const { currentTab, handleTabChange: setCurrentTab } = useTabs<typeof WordlistTab>(WordlistTab.LIST);

  const [wordlist, setWordlist] = useState<Wordlist>();
  const [switched, setSwitched] = useState<boolean>(false);
  const [cardsExpanded, setCardsExpanded] = useState<boolean>(false);

  const [selectedWord, setSelectedWord] = useState<EnrichedWord>();

  const signedUrls = useMemo<SignedUrlsByWord>(() => ({}), []);

  const handleSelectedWord = useCallback(
    async (word?: EnrichedWord) => {
      if (!word) return setSelectedWord(undefined);

      try {
        const { id, image, maleVoice, femaleVoice } = word;

        const signedUrlsToFetch = [];
        if (image && !signedUrls[id]?.[image]) signedUrlsToFetch.push(image);
        if (maleVoice && !signedUrls[id]?.[maleVoice]) signedUrlsToFetch.push(maleVoice);
        if (femaleVoice && !signedUrls[id]?.[femaleVoice]) signedUrlsToFetch.push(femaleVoice);

        if (signedUrlsToFetch.length > 0) {
          const { data: fetchedUrls } = await getSignedUrls(signedUrlsToFetch);

          signedUrls[id] = {
            ...signedUrls[id],
            ...fetchedUrls,
          };
        }

        setSelectedWord({
          ...word,
          s3Image: image ? signedUrls[id]?.[image] : undefined,
          s3MaleVoice: maleVoice ? signedUrls[id]?.[maleVoice] : undefined,
          s3FemaleVoice: femaleVoice ? signedUrls[id]?.[femaleVoice] : undefined,
        });
      } catch (e) {
        handleError(e);
      }
    },
    [handleError, signedUrls],
  );

  const { searchResults, searchTerm, setSearchTerm, searchError } = useWordlistSearch({
    wordlist,
    switched,
    setSelectedWord: handleSelectedWord,
    currentTab,
  });

  const { favoriteIds, clearFavoriteWords, toggleFavoriteWord } = useWordlistFavorites({
    wordlist,
    currentTab,
    setSelectedWord: handleSelectedWord,
    selectedWord,
    module,
  });

  useEffect(() => {
    const fetchWordlist = async () => {
      if (!wordlistId) return;

      try {
        const wordlist = await getWordlist(module.id, wordlistId);

        setWordlist(wordlist);
        handleSelectedWord(wordlist.words[0]);
      } catch (e) {
        handleError(e);
      }
    };

    fetchWordlist();
  }, [handleError, handleSelectedWord, module, wordlistId]);

  const handleTabChange = useCallback(
    (tab: typeof WordlistTab[keyof typeof WordlistTab]) => {
      if (tab === currentTab) return;

      setCurrentTab(tab);

      if (!wordlist) return;

      if (tab === WordlistTab.SEARCH) return setSelectedWord(undefined);

      if (tab === WordlistTab.FAVORITES) {
        return handleSelectedWord(wordlist.words.find(word => favoriteIds.includes(word.id)));
      }

      return handleSelectedWord(wordlist.words[0]);
    },
    [currentTab, favoriteIds, handleSelectedWord, setCurrentTab, wordlist],
  );

  // favoriteIds is fetched asynchronously, so switching to the favorites tab can happen before it has
  // loaded, leaving no word selected. Once favoriteIds arrives, select the first favorited word if the
  // favorites tab is still active and nothing has been selected since.
  useEffect(() => {
    if (currentTab !== WordlistTab.FAVORITES) return;
    if (selectedWord) return;
    if (!wordlist || favoriteIds.length === 0) return;

    handleSelectedWord(wordlist.words.find(word => favoriteIds.includes(word.id)));
  }, [currentTab, favoriteIds, handleSelectedWord, selectedWord, wordlist]);

  const switchLanguages = useCallback(() => {
    setSwitched(!switched);
  }, [switched]);

  const showPreviousWord = useCallback(
    (list: EnrichedWord[]) => {
      if (!selectedWord) return;

      const selectedWordIndex = list.findIndex(word => word.id === selectedWord.id);

      if (selectedWordIndex === 0) return;

      return handleSelectedWord(list[selectedWordIndex - 1]);
    },
    [handleSelectedWord, selectedWord],
  );

  const showNextWord = useCallback(
    (list: EnrichedWord[]) => {
      if (!selectedWord) return;

      const selectedWordIndex = list.findIndex(word => word.id === selectedWord.id);

      if (selectedWordIndex === list.length - 1) return;

      return handleSelectedWord(list[selectedWordIndex + 1]);
    },
    [handleSelectedWord, selectedWord],
  );

  return (
    <WordlistContext.Provider
      value={{
        wordlist,
        favoriteIds,
        switched,
        switchLanguages,
        selectedWord,
        setSelectedWord: handleSelectedWord,
        toggleFavoriteWord,
        clearFavoriteWords,
        currentTab,
        handleTabChange,
        showPreviousWord,
        showNextWord,
        searchTerm,
        setSearchTerm,
        searchError,
        searchResults,
        cardsExpanded,
        setCardsExpanded,
        module,
      }}
    >
      {children}
    </WordlistContext.Provider>
  );
}
